import { createFileRoute, Link, useNavigate, notFound } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Flag, Heart, Pin, Trash2, MessageSquarePlus } from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { FloatingPortal } from "@/components/ui/floating-portal";
import { Linkify } from "@/components/Linkify";
import { ReportDialog } from "@/components/groups/ReportDialog";

import { useCurrentUser } from "@/hooks/useCurrentUser";
import {
  getGroupTopic, listPostComments, createPostComment, togglePostReaction,
  pinPost, deleteGroupPost, getMyGroupMembership,
} from "@/lib/groups.functions";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/comunidades/$slug/topico/$id")({
  head: ({ loaderData }) => {
    const t = loaderData as any;
    const title = t?.title ? `${t.title} — Comunidades Viny Club` : "Tópico — Comunidades Viny Club";
    const desc = (t?.body || "Conversa da comunidade de colecionadores de vinil.").slice(0, 155);
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
      ],
    };
  },
  loader: async ({ params }) => {
    const { getGroupTopic } = await import("@/lib/groups.functions");
    const t = await getGroupTopic({ data: { topicId: params.id } });
    if (!t) throw notFound();
    return t;
  },
  component: TopicoPage,
  notFoundComponent: () => (
    <AppShell>
      <div className="mx-auto max-w-md px-4 py-20 text-center text-sm text-muted-foreground">
        Tópico não encontrado.
      </div>
    </AppShell>
  ),
  errorComponent: ({ error }) => (
    <AppShell>
      <div className="mx-auto max-w-md px-4 py-20 text-center text-sm text-muted-foreground">{(error as Error).message}</div>
    </AppShell>
  ),
});

function fmt(iso: string) {
  return new Date(iso).toLocaleString("pt-BR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function TopicoPage() {
  const initial = Route.useLoaderData() as any;
  const { slug, id } = Route.useParams();
  const { profile, isAuthenticated } = useCurrentUser();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const fetchTopic = useServerFn(getGroupTopic);
  const fetchComments = useServerFn(listPostComments);
  const addComment = useServerFn(createPostComment);
  const react = useServerFn(togglePostReaction);
  const pin = useServerFn(pinPost);
  const del = useServerFn(deleteGroupPost);
  const fetchMembership = useServerFn(getMyGroupMembership);

  const topicQ = useQuery({
    queryKey: ["group-topic", id, profile?.id],
    queryFn: () => fetchTopic({ data: { topicId: id, userId: profile?.id } }),
    initialData: initial,
  });
  const topic = topicQ.data ?? initial;

  const memQ = useQuery({
    queryKey: ["group-membership", topic.group_id],
    queryFn: () => fetchMembership({ data: { groupId: topic.group_id } }),
    enabled: isAuthenticated,
  });
  const isMember = !!memQ.data;
  const isMod = memQ.data?.role === "admin" || memQ.data?.role === "moderator";
  const isOwner = topic.user_id === profile?.id;

  const commentsQ = useQuery({
    queryKey: ["group-post-comments", id],
    queryFn: () => fetchComments({ data: { postId: id } }),
  });

  const [body, setBody] = useState("");
  const [reportOpen, setReportOpen] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);

  const replyMut = useMutation({
    mutationFn: () => addComment({ data: { postId: id, body } }),
    onSuccess: () => {
      setBody("");
      setComposeOpen(false);
      qc.invalidateQueries({ queryKey: ["group-post-comments", id] });
      qc.invalidateQueries({ queryKey: ["group-topic", id] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const submitReply = () => {
    if (body.trim().length < 2) return toast.error("Escreva sua resposta");
    replyMut.mutate();
  };


  const reactMut = useMutation({
    mutationFn: () => react({ data: { postId: id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["group-topic", id] }),
  });

  const pinMut = useMutation({
    mutationFn: () => pin({ data: { postId: id, pinned: !topic.pinned } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["group-topic", id] }),
  });

  const delMut = useMutation({
    mutationFn: () => del({ data: { postId: id } }),
    onSuccess: () => {
      toast.success("Tópico removido");
      navigate({ to: "/comunidades/$slug", params: { slug } });
    },
  });

  return (
    <AppShell>
      <div className="mx-auto w-full max-w-3xl px-4 pb-44 md:pb-16">
        <Link
          to="/comunidades/$slug"
          params={{ slug }}
          className="mb-3 inline-flex items-center gap-1.5 text-[13px] text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Voltar ao fórum
        </Link>

        <article className="rounded-xl border border-[color:var(--hairline)] bg-surface/40 p-4">
          <div className="mb-2 flex items-start gap-2">
            {topic.pinned && <Pin className="mt-1 h-4 w-4 shrink-0 text-primary" />}
            <h1 className="font-display text-lg font-semibold leading-snug sm:text-xl">
              {topic.title || "Tópico"}
            </h1>
          </div>
          <div className="mb-3 flex items-center gap-2 text-[12px] text-muted-foreground">
            <img
              loading="lazy"
              decoding="async"
              src={topic.author?.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${topic.author?.username ?? "u"}`}
              alt=""
              className="h-6 w-6 rounded-md object-cover"
            />
            <span>@{topic.author?.username ?? "?"}</span>
            <span>·</span>
            <span>{fmt(topic.created_at)}</span>
          </div>

          {topic.image_urls?.[0] && (
            <img src={topic.image_urls[0]} alt="" loading="lazy" className="mb-3 max-h-[520px] w-full rounded-lg object-cover" />
          )}
          {topic.body && (
            <p className="whitespace-pre-wrap text-[14px] leading-relaxed">
              <Linkify text={topic.body} />
            </p>
          )}

          <div className="mt-4 flex items-center gap-3 text-[12.5px] text-muted-foreground">
            <button
              type="button"
              onClick={() => (isAuthenticated ? reactMut.mutate() : navigate({ to: "/auth" }))}
              className={cn("inline-flex items-center gap-1 hover:text-red-500", topic.liked_by_me && "text-red-500")}
            >
              <Heart className={cn("h-4 w-4", topic.liked_by_me && "fill-current")} /> {topic.likes_count}
            </button>
            <button
              type="button"
              onClick={() => setReportOpen(true)}
              className="inline-flex items-center gap-1 hover:text-foreground"
            >
              <Flag className="h-3.5 w-3.5" /> denunciar
            </button>
            <div className="ml-auto flex items-center gap-2">
              {isMod && (
                <button type="button" onClick={() => pinMut.mutate()} className="inline-flex items-center gap-1 hover:text-foreground">
                  <Pin className={cn("h-3.5 w-3.5", topic.pinned && "text-primary")} />
                  {topic.pinned ? "desafixar" : "fixar"}
                </button>
              )}
              {(isMod || isOwner) && (
                <button type="button" onClick={() => delMut.mutate()} className="inline-flex items-center gap-1 hover:text-destructive">
                  <Trash2 className="h-3.5 w-3.5" /> remover
                </button>
              )}
            </div>
          </div>
        </article>

        <h2 className="mb-2 mt-6 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          Respostas ({commentsQ.data?.length ?? 0})
        </h2>

        <div className="space-y-2">
          {(commentsQ.data ?? []).map((c: any) => (
            <div key={c.id} className="rounded-xl border border-[color:var(--hairline)] bg-surface/40 p-3">
              <div className="mb-1 flex items-center gap-2 text-[12px] text-muted-foreground">
                <img
                  loading="lazy"
                  decoding="async"
                  src={c.author?.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${c.author?.username ?? "u"}`}
                  alt=""
                  className="h-6 w-6 rounded-md object-cover"
                />
                <span>@{c.author?.username ?? "?"}</span>
                <span>·</span>
                <span>{fmt(c.created_at)}</span>
              </div>
              {c.media_url && (
                <img src={c.media_url} alt="" loading="lazy" className="mb-2 max-h-64 rounded-lg object-cover" />
              )}
              <p className="whitespace-pre-wrap text-[13.5px] leading-relaxed">
                <Linkify text={c.body} />
              </p>
            </div>
          ))}
          {(commentsQ.data?.length ?? 0) === 0 && !commentsQ.isLoading && (
            <p className="text-[13px] text-muted-foreground">Ninguém respondeu ainda.</p>
          )}
        </div>

        {isMember ? (
          <div className="mt-4 hidden space-y-2 md:block">
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder="Escreva sua resposta..."
              className="w-full rounded-xl border border-[color:var(--hairline)] bg-surface/60 px-4 py-3 text-sm focus:border-primary/60 focus:outline-none"
            />
            <button
              type="button"
              onClick={submitReply}
              disabled={replyMut.isPending}
              className="h-10 rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground disabled:opacity-50"
            >
              {replyMut.isPending ? "Enviando..." : "Responder"}
            </button>
          </div>
        ) : (
          <p className="mt-4 text-[13px] text-muted-foreground">
            {isAuthenticated ? (
              <>Participe da comunidade para responder.</>
            ) : (
              <Link to="/auth" className="text-primary hover:underline">Entre na sua conta</Link>
            )}
          </p>
        )}
      </div>

      {/* Barra de resposta fixa no mobile */}
      {isMember && (
        <FloatingPortal>
          <div className="fixed inset-x-0 bottom-0 z-[60] border-t border-foreground/10 bg-background/95 px-4 pb-[calc(env(safe-area-inset-bottom)+76px)] pt-3 backdrop-blur-xl md:hidden">
            <button
              type="button"
              onClick={() => setComposeOpen(true)}
              className="flex h-12 w-full items-center gap-2 rounded-full border border-foreground/10 bg-surface px-4 text-left text-sm text-muted-foreground"
            >
              <MessageSquarePlus className="h-4 w-4 shrink-0" />
              Escrever uma resposta...
            </button>
          </div>
        </FloatingPortal>
      )}

      <Sheet open={composeOpen} onOpenChange={setComposeOpen}>
        <SheetContent side="bottom" className="flex h-[92dvh] flex-col overflow-y-auto rounded-t-3xl border-foreground/10 px-4 pb-[calc(env(safe-area-inset-bottom)+24px)] pt-3">
          <div className="mx-auto mb-4 h-1 w-10 shrink-0 rounded-full bg-foreground/20" />
          <SheetHeader className="shrink-0 text-left"><SheetTitle>Responder</SheetTitle></SheetHeader>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={5}
            maxLength={1000}
            autoFocus
            placeholder="Escreva sua resposta..."
            className="mt-4 w-full shrink-0 resize-none rounded-2xl border border-foreground/10 bg-surface px-4 py-3 text-base outline-none focus:border-foreground/25"
          />
          <button
            type="button"
            onClick={submitReply}
            disabled={replyMut.isPending}
            className="mt-3 h-12 w-full shrink-0 rounded-full bg-primary text-sm font-semibold text-primary-foreground disabled:opacity-50"
          >
            {replyMut.isPending ? "Enviando..." : "Publicar resposta"}
          </button>
        </SheetContent>
      </Sheet>


      <ReportDialog open={reportOpen} onOpenChange={setReportOpen} groupId={topic.group_id} postId={id} />


    </AppShell>
  );
}
